import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, ChevronUp, Sparkles, Check, Bell, ArrowRight,
  Shield, Lock, Fingerprint, Zap, RefreshCw, KeyRound
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Data ────────────────────────────────────────── */

const challengeSteps = [
  { num: 1, icon: "🔢", title: "Passcode", desc: "Type your 4-digit daily passcode — or upgrade it to an \"I am\" affirmation (see Mindset)." },
  { num: 2, icon: "➕", title: "Math", desc: "One simple problem. The conscious mind wakes up; the impulse loses its grip." },
  { num: 3, icon: "🌬️", title: "Box breathing", desc: "30 seconds of 4-2-4-2 breathing. Drops your heart rate before you engage." },
  { num: 4, icon: "✍️", title: "Reflection", desc: "\"Why do you want to open this right now?\" — AI-tailored to the app and your goals." },
  { num: 5, icon: "⏳", title: "Wait", desc: "A forced 15-second pause. Most urges don\u2019t survive it." },
];

const coreFeatures = [
  { icon: "📱", title: "Tracked apps", desc: "Curate the apps you want gated. Add Instagram, TikTok, Snap, YouTube — anything.", how: "Profile → Tracked apps" },
  { icon: "🎯", title: "Daily goal", desc: "Set a screen-time target you actually believe in. The whole app rallies around it.", how: "Profile → Daily goal minutes" },
  { icon: "👆", title: "Tap counter", desc: "Counts every time you reach for a tracked app — awareness is half the change.", how: "Stats → Urges you noticed" },
];

const studioCards = [
  { eyebrow: "vibe archetypes", title: "Pick the energy you\u2019re locking into.", desc: "Choose one of four personas. We tune every block screen, affirmation, and (soon) your Dream Trailer template to the version of you already trying to come out.", how: "Profile → Mindset → Your vibe", hero: true, vibes: ["📚 lock in", "🔥 aesthetic grind", "✨ main character", "🎨 creative hustler"] },
  { eyebrow: "affirmation lock", title: "Type your way in.", desc: "Instead of a 4-digit code, you type a present-tense \"I am\" phrase. The physical act of typing rewires the passive impulse.", how: "Profile → Mindset → Affirmation lock", typed: "i am in control of my time…" },
  { eyebrow: "dream trailer", title: "Your real life. 5 seconds.", desc: "Tap Instagram, see a full-screen 9:16 montage of your goals — gym PRs, dream campus, art, friends. A massive \"continue building\" CTA. Bypass requires a 10-second hold-down.", how: "Profile → Mindset → Dream trailer · upload up to 5 photos or short clips" },
  { eyebrow: "chill gate", title: "Drop the heart rate first.", desc: "If you do tap \"bypass anyway\", we force box-breathing as step 1 of the challenge. Your nervous system calms before you scroll — and most of the time, you just close the app.", how: "Automatic · no setting required" },
  { eyebrow: "redirect engine", title: "The urge needs somewhere to go.", desc: "You can\u2019t erase a thought — you replace it. Bail mid-challenge and we offer one random fact, a 30-second SVG doodle, or three box-breath cycles.", how: "Automatic on cancel · also reachable from the home tab" },
  { eyebrow: "golden hour", title: "Guard your most receptive minds.", desc: "The hour before bed and the hour after waking are when your subconscious is wide open. Sva soft-locks tracked apps in those windows with a warm, breathing-graphic screen.", how: "Profile → Mindset → Golden hour · pick bedtime + wake time", hero: true },
  { eyebrow: "identity-first stats", title: "Celebrate time reclaimed.", desc: "Most apps shame you with \"you wasted 6 hours\". Sva flips the script: \"You reclaimed 4h 18m this week.\" When you see yourself as a focused person, the behaviour follows.", how: "Automatic · stats tab hero" },
];

const routingSteps = [
  { title: "Golden Hour", desc: "Inside bedtime−1h or wake+1h → calming breath screen. No bypass." },
  { title: "Dream Trailer", desc: "If enabled with ≥ 1 clip → full-screen montage of your real life. \"Lock in\" or 10-sec hold to bypass." },
  { title: "Multi-step Challenge", desc: "The classic five steps. With the Chill Gate added if you came from the Trailer bypass." },
];

const teenTools = [
  { icon: "☁️", title: "Virtual pet — Cumulo", desc: "A small AI-generated companion that gets happier the longer you stay under your daily goal.", how: "/pet · also on Profile" },
  { icon: "🏃", title: "Step goal unlock", desc: "Earn unlock minutes by walking. Pulls device steps automatically — no fitness band required.", how: "/step-goal · set a daily step goal" },
  { icon: "🤝", title: "Squad accountability", desc: "Form a 2–6 person squad. Members can see each other\u2019s daily focus stats and cheer each other on.", how: "/squads · create or join" },
];

const coachFeatures = [
  { icon: "💬", title: "Chat coach", desc: "Multi-turn conversation. Ask anything — \"I keep failing my goal at 11pm, why?\" — and get a personal, kind response.", how: "Home tab → Talk to Sva" },
  { icon: "🌅", title: "Daily tip", desc: "A morning suggestion tuned to your usage and goals. One small move you can actually take today.", how: "Auto-shown on the home tab" },
  { icon: "🪞", title: "Smart reflection", desc: "The reflection step of every challenge is tailored by AI to the specific app and your goal — never a generic prompt.", how: "Inside the challenge flow" },
];

const dailyRituals = [
  { icon: "🌱", title: "Daily intention", desc: "One sentence each morning. \"Today I am building X.\" The day organises itself around it.", how: "Home → Intention card" },
  { icon: "😌", title: "Mood check-in", desc: "Tap how you feel. Over weeks, you\u2019ll see which moods drive scrolling — and which don\u2019t.", moods: ["😄", "😊", "😐", "😔", "😩"] },
  { icon: "🎯", title: "Focus sessions", desc: "Self-started timed deep-work blocks (Pomodoro-style). Pairs with the daily goal to lock you in.", how: "Home → Start focus" },
];

const statCards = [
  { icon: "🏆", title: "Identity-first hero", desc: "\"You reclaimed Xh Ym this week\" — tied to your vibe." },
  { icon: "📈", title: "7-day chart", desc: "Your screen-time vs your goal, day by day. Quietly proves you\u2019re doing this." },
  { icon: "🎯", title: "Urge counter", desc: "Every tracked-app tap recorded — sorted most to least. Noticing is half of changing it." },
];

const securityCards = [
  { icon: "🔐", title: "Lock password", desc: "A separate privileged password protects tracked-app changes and disables. Even uninstall on Android.", how: "Profile → Lock password" },
  { icon: "🪪", title: "Biometric unlock", desc: "Face ID / fingerprint re-opens the app instantly. Optional, off by default.", how: "Profile → Biometric" },
  { icon: "🛡️", title: "Tamper protection", desc: "Android Device Admin prevents uninstall without your Lock password.", how: "Profile → Tamper protection (Android)" },
  { icon: "🚦", title: "Rate-limited login", desc: "Brute-force protection on login and password reset, per IP and per email. Constant-time comparisons.", how: "Automatic" },
  { icon: "🔁", title: "Instant token revocation", desc: "Sign out invalidates the JWT across every device on the spot.", how: "Sign out from Profile" },
  { icon: "🔏", title: "Encrypted at rest", desc: "bcrypt-hashed passwords. HMAC-verified Razorpay webhooks. TLS 1.2+ in transit.", how: "Automatic" },
];

const planFeatures = [
  "Unlimited tracked apps & daily goals",
  "Mindset Studio — vibe, affirmation, trailer, golden hour, redirect",
  "AI coach & smart reflection",
  "Teen tools — pet, step goal, squad",
  "Identity-first stats & tap counter",
  "Biometric + Lock password + Device Admin",
  "GST-compliant tax invoice",
];

const faqs = [
  { q: "How is Sva different from Apple Screen Time or Digital Wellbeing?", a: "Those are dials. Sva is a coach. It uses psychology-driven friction (challenges, breath, your own goals played as a montage) to actually change the impulse — not just count it. And the Mindset Studio works the same way on iOS and Android." },
  { q: "Does the Dream Trailer really play before TikTok or Instagram?", a: "Inside the app, yes — and on a native Android build, the trailer overlays the system when you tap the real TikTok or Instagram (via Accessibility Service + display-over-other-apps). On iOS, Apple doesn\u2019t allow app-launch interception by any third party, so the trailer plays when you open Sva first." },
  { q: "Will my teen just delete Sva to bypass it?", a: "On Android, Device Admin tamper protection requires the Lock password to uninstall — so a parent or the user themselves (when they\u2019re calmer) controls removal. On iOS, the same is achieved by enabling Screen Time restrictions to require a passcode for deletions." },
  { q: "What about my data?", a: "Sva is DPDP Act 2023 compliant. We don\u2019t run ads, don\u2019t sell data, don\u2019t share advertising identifiers. Card details never touch our servers — they go to Razorpay. AI conversations only send your message + minimal context (name, today\u2019s screen-time, goal) to the model." },
  { q: "What\u2019s the refund policy?", a: "The 7-day trial is the evaluation — you can cancel before day 8 and pay \u20B90. After that we don\u2019t pro-rate refunds, except for documented duplicate charges, unauthorised charges, or 72+ hour service outages." },
  { q: "Does the AI coach really help, or is it generic?", a: "The coach knows your vibe archetype, your current daily goal, what apps you\u2019re trying to scroll right now, and how you logged your mood — so the answers are about you. The reflection step in challenges is also re-prompted by AI for the exact app you\u2019re about to open." },
];

const trustItems = [
  "🇮🇳 Made in India",
  "🧾 GST tax invoices",
  "💳 Razorpay UPI AutoPay",
  "🤝 No ads · No data sale",
];

/* ── Components ────────────────────────────────────── */

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800/60" data-testid="faq-item">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group">
        <span className="font-semibold text-white pr-4 group-hover:text-cyan-400 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>{q}</span>
        {open ? <ChevronUp className="text-cyan-400 flex-shrink-0" size={20} /> : <ChevronDown className="text-slate-500 flex-shrink-0" size={20} />}
      </button>
      {open && <p className="pb-5 text-slate-400 text-sm leading-relaxed">{a}</p>}
    </div>
  );
}

function FeatureCard({ icon, title, desc, how, moods, children }) {
  return (
    <div className="card-glass" data-testid="feat-card">
      <span className="text-3xl mb-4 block">{icon}</span>
      <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed mb-3">{desc}</p>
      {moods && <div className="flex gap-2 text-2xl mb-3">{moods.map(m => <span key={m}>{m}</span>)}</div>}
      {children}
      {how && <p className="text-xs text-slate-600 mt-auto pt-2">{how}</p>}
    </div>
  );
}

function SectionHead({ overline, title, subtitle }) {
  return (
    <div className="text-center mb-16">
      <span className="caption mb-4 block">{overline}</span>
      <h2 className="heading-2 text-white mb-4">{title}</h2>
      {subtitle && <p className="body-large max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
}

/* ── Page ────────────────────────────────────────── */

export default function ProductsPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/products/notify`, { email });
      if (response.data.success) { toast.success(response.data.message); setEmail(""); }
      else { toast.info(response.data.message); }
    } catch (err) { console.error("Notify error:", err); toast.error("Failed to register. Please try again."); }
    finally { setIsLoading(false); }
  };

  return (
    <div className="noise-bg min-h-screen pt-20" data-testid="products-page">

      {/* ═══ HERO ═══ */}
      <section className="section-padding relative overflow-hidden" data-testid="sva-hero">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-cyan-500/8 rounded-full blur-[140px]" />
        <div className="container-custom relative z-10 text-center max-w-3xl mx-auto">
          <span className="badge mb-6 inline-flex" data-testid="sva-badge">
            <Sparkles className="w-3 h-3 mr-1" /> A BastionGuard Product
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="sva-title">
            Stop scrolling.<br />Build the life you <span className="gradient-text">actually want</span>.
          </h1>
          <p className="text-lg text-slate-400 mb-8 leading-relaxed max-w-xl mx-auto" data-testid="sva-subtitle">
            Sva — Detox is a psychology-driven mobile app that helps you stop scrolling and build the life you actually want. Multi-step challenges, AI coach, Dream Trailer intercepts, Golden Hour soft-locks. ₹199/month after a 7-day free trial.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <Button className="btn-primary text-base px-8 py-6 flex items-center gap-2" data-testid="sva-cta-trial">
              Start 7-day free trial <ArrowRight size={18} />
            </Button>
            <a href="#detox">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-base px-8 py-6">See how it works</Button>
            </a>
          </div>
        </div>
      </section>

      {/* ═══ TRUST STRIP ═══ */}
      <div className="border-y border-slate-800/50 py-4" data-testid="trust-strip">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-slate-500">
            {trustItems.map(item => <span key={item}>{item}</span>)}
          </div>
        </div>
      </div>

      {/* ═══ DETOX CORE — 5 Steps ═══ */}
      <section id="detox" className="section-padding" data-testid="sva-detox-core">
        <div className="container-custom">
          <SectionHead overline="The stop-scrolling engine" title="Five tiny acts of friction." subtitle="Tapping a tracked app doesn't open it — it opens a brief, configurable challenge. Each step you complete is a moment to remember what you actually meant to do." />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
            {challengeSteps.map(s => (
              <div key={s.title} className="card-glass text-center relative" data-testid={`step-${s.num}`}>
                <span className="absolute top-3 left-3 text-xs font-bold text-cyan-500/50">{s.num}</span>
                <span className="text-3xl block mb-3">{s.icon}</span>
                <h3 className="font-semibold text-white text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coreFeatures.map(f => <FeatureCard key={f.title} {...f} />)}
          </div>
        </div>
      </section>

      {/* ═══ MINDSET STUDIO ═══ */}
      <section id="studio" className="section-padding relative" style={{ background: 'linear-gradient(180deg, #020617 0%, #0B1120 50%, #020617 100%)' }} data-testid="sva-studio">
        <div className="container-custom">
          <SectionHead overline="The mindset studio" title="Seven psychology-driven layers under the lockscreen." subtitle="Every other detox app fights the conscious mind with willpower. Sva talks to the engine room — habit, emotion, imagery — using a decades-old playbook on how the subconscious actually changes." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {studioCards.map(c => (
              <div key={c.eyebrow} className={`card-glass ${c.hero ? 'md:col-span-2 lg:col-span-2' : ''}`} data-testid={`studio-${c.eyebrow.replace(/\s/g, '-')}`}>
                <p className="text-[11px] font-bold tracking-[2px] text-cyan-400 uppercase mb-2">{c.eyebrow}</p>
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">{c.desc}</p>
                {c.vibes && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {c.vibes.map(v => <span key={v} className="px-3 py-1.5 rounded-full text-xs font-medium bg-slate-800 text-slate-300 border border-slate-700/50">{v}</span>)}
                  </div>
                )}
                {c.typed && (
                  <div className="bg-slate-800/60 rounded-xl p-4 border border-slate-700/50 mb-3">
                    <p className="text-sm text-slate-300 italic font-mono">{c.typed}</p>
                  </div>
                )}
                <p className="text-xs text-slate-600 mt-auto">{c.how}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ROUTING PRECEDENCE ═══ */}
      <section className="py-12" data-testid="sva-routing">
        <div className="container-custom max-w-3xl">
          <div className="card-glass p-8 md:p-10">
            <span className="caption mb-3 block">When you tap a tracked app</span>
            <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Three layers, in order.</h3>
            <p className="text-sm text-slate-400 mb-6">Sva picks the right friction for the moment — calming when you{"'"}re tired, vivid when you need a reminder of what matters, structured when you just need a beat.</p>
            <div className="flex flex-col md:flex-row gap-4">
              {routingSteps.map((s, i) => (
                <div key={s.title} className="flex-1 relative">
                  <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/40 h-full">
                    <span className="text-xs font-bold text-cyan-500/60 block mb-2">{i + 1}</span>
                    <h4 className="font-semibold text-white text-sm mb-1">{s.title}</h4>
                    <p className="text-xs text-slate-500">{s.desc}</p>
                  </div>
                  {i < routingSteps.length - 1 && <div className="hidden md:block absolute top-1/2 -right-3 text-slate-700">→</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TEEN TOOLS ═══ */}
      <section className="section-padding" data-testid="sva-teen">
        <div className="container-custom">
          <SectionHead overline="Teen tools" title="Lightweight gamification, no cringe." subtitle="For younger users, every focused hour earns something real. Adults can flip these on too." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {teenTools.map(f => <FeatureCard key={f.title} {...f} />)}
          </div>
        </div>
      </section>

      {/* ═══ AI COACH ═══ */}
      <section className="section-padding relative" style={{ background: 'linear-gradient(180deg, #020617 0%, #0d1424 100%)' }} data-testid="sva-coach">
        <div className="container-custom">
          <SectionHead overline="AI coach · Sva" title="A pocket coach trained on what actually helps." subtitle="Powered by Claude Sonnet via Emergent. Talks to you about the urge in front of you — not generic mindfulness platitudes." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coachFeatures.map(f => <FeatureCard key={f.title} {...f} />)}
          </div>
        </div>
      </section>

      {/* ═══ DAILY RITUALS ═══ */}
      <section className="section-padding" data-testid="sva-rituals">
        <div className="container-custom">
          <SectionHead overline="Daily rituals" title="One minute of intention beats 60 of regret." subtitle="Tiny daily inputs that compound into a different relationship with your phone." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dailyRituals.map(f => <FeatureCard key={f.title} {...f} />)}
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section className="section-padding" style={{ background: 'var(--bg, #020617)' }} data-testid="sva-stats">
        <div className="container-custom">
          <SectionHead overline="Stats tab" title="Built to make you feel capable, not guilty." subtitle="Every chart on Sva is a reminder of capacity, not a tally of failure." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {statCards.map(f => <FeatureCard key={f.title} {...f} />)}
          </div>
        </div>
      </section>

      {/* ═══ SECURITY ═══ */}
      <section className="section-padding" data-testid="sva-security">
        <div className="container-custom">
          <SectionHead overline="Security & trust" title="Locked down by default. Yours by design." subtitle="Sva is yours, not ours. Built for India's DPDP Act 2023, with the security details we'd want for our own kids." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityCards.map(f => <FeatureCard key={f.title} {...f} />)}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="section-padding" data-testid="sva-pricing">
        <div className="container-custom max-w-xl mx-auto text-center">
          <SectionHead overline="Pricing" title="One plan. No upsells." subtitle="Everything in Sva — including the Mindset Studio, AI coach, Dream Trailer and Golden Hour — for one calm price." />
          <div className="card-glass rounded-2xl p-8 border border-cyan-500/20 text-left" data-testid="sva-plan-card">
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 mb-4">🌿 7 days free · cancel anytime</span>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-5xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>₹199</span>
              <span className="text-slate-500 text-sm">/ month</span>
            </div>
            <p className="text-xs text-slate-600 mb-6">First 7 days free · ₹0 during trial</p>
            <ul className="space-y-3 mb-8">
              {planFeatures.map(f => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />{f}
                </li>
              ))}
            </ul>
            <Button className="w-full py-6 text-base font-semibold bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="sva-plan-cta">
              Start your free trial <ArrowRight size={18} className="ml-2" />
            </Button>
            <p className="text-xs text-slate-600 mt-4 text-center">Auto-renews at ₹199/month (GST inclusive). Cancel from Profile any time. UPI · Card · Netbanking via Razorpay.</p>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="section-padding" data-testid="sva-faq">
        <div className="container-custom max-w-3xl">
          <SectionHead overline="FAQ" title="Honest answers." />
          {faqs.map(f => <FAQItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* ═══ NOTIFY ME ═══ */}
      <section className="pb-16" data-testid="notify-section">
        <div className="container-custom">
          <div className="max-w-xl mx-auto glass-card rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[100px] bg-cyan-500/30 rounded-full blur-[60px]" />
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center animate-glow-pulse">
                <Bell className="w-10 h-10 text-cyan-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Get Launch Updates</h2>
              <p className="body-base mb-6">Enter your email to get notified when Sva — Detox is live on the Play Store.</p>
              <form onSubmit={handleNotifySubmit} className="flex flex-col sm:flex-row gap-3" data-testid="notify-form">
                <Input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark flex-1" data-testid="notify-email-input" />
                <Button type="submit" className="btn-primary" disabled={isLoading} data-testid="notify-submit-btn">
                  {isLoading ? "Submitting..." : "Notify Me"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ LEGAL LINKS ═══ */}
      <section className="pb-12" data-testid="sva-legal-links">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/products/sva-detox/privacy" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="link-privacy">Privacy Policy</Link>
            <span className="text-slate-600">·</span>
            <Link to="/products/sva-detox/terms" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="link-terms">Terms of Service</Link>
            <span className="text-slate-600">·</span>
            <Link to="/products/sva-detox/refunds" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="link-refunds">Refund Policy</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
